import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MediaPlayer } from "../components/MediaPlayer";
import { NotificationsPanel, ReferralCard, VotingPanel, LeaderboardPanel, PayoutCalendar } from "../components/MemberPanels";
import { api, money, COURSE_IMAGES } from "../lib/data";
import { useAuth } from "../context/AuthContext";
import { Clock, CheckCircle2, XCircle, Wallet, ArrowRight } from "lucide-react";

function PayoutTracker({ purchase }) {
  const paid = new Set(purchase.paid_weeks || []);
  const paidCount = paid.size;
  const earned = paidCount * purchase.weekly;
  return (
    <div className="border-t border-gray-200 mt-4 pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0A1914]"><Wallet className="h-4 w-4 text-[#00B368]" /> Weekly payout schedule</div>
        <p className="text-xs text-gray-500 font-mono">{money(earned)} paid · {paidCount}/52 weeks</p>
      </div>
      <div className="grid grid-cols-13 gap-1" style={{ gridTemplateColumns: "repeat(13, minmax(0, 1fr))" }}>
        {Array.from({ length: 52 }, (_, i) => i + 1).map((w) => (
          <div key={w} title={`Week ${w} · ${money(purchase.weekly)}`} className={`h-4 rounded-[2px] ${paid.has(w) ? "bg-[#00B368]" : "bg-gray-200"}`} data-testid={`week-cell-${purchase.id}-${w}`} />
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">{money(purchase.weekly)}/week · paid every Friday before 4pm EST</p>
    </div>
  );
}

export default function Members() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/purchases/me").then((r) => { setPurchases(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const approved = purchases.filter((p) => p.status === "approved");
  const pending = purchases.filter((p) => p.status === "pending");
  const totalEarned = approved.reduce((s, p) => s + (p.paid_weeks?.length || 0) * p.weekly, 0);

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#00B368] mb-2">Members Area</p>
            <h1 className="font-display text-4xl tracking-tight text-[#0A1914]">Welcome, {user?.name || "Member"}</h1>
          </div>
          <div className="flex gap-4">
            <div className="border border-gray-200 bg-white px-5 py-3"><p className="text-xs text-gray-500">Active courses</p><p className="font-mono text-2xl text-[#0A1914]">{approved.length}</p></div>
            <div className="border border-gray-200 bg-white px-5 py-3"><p className="text-xs text-gray-500">Total earned</p><p className="font-mono text-2xl text-[#00B368]">{money(totalEarned)}</p></div>
          </div>
        </div>

        {loading ? <p className="text-gray-500">Loading…</p> : (
          <>
            <NotificationsPanel />
            {purchases.length === 0 && (
              <div className="border border-dashed border-gray-300 p-12 text-center">
                <p className="text-gray-600 mb-4">You haven't enrolled in any courses yet.</p>
                <Link to="/courses" className="inline-flex items-center gap-2 bg-[#0A1914] text-white px-5 py-2.5 rounded-sm hover:bg-[#00B368] transition-colors">Browse courses <ArrowRight className="h-4 w-4" /></Link>
              </div>
            )}

            {pending.length > 0 && (
              <div className="mb-10">
                <h2 className="font-display text-2xl text-[#0A1914] mb-4">Pending approval</h2>
                <div className="space-y-3">
                  {pending.map((p) => (
                    <div key={p.id} className="border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-5 py-4 flex items-center justify-between" data-testid={`pending-${p.id}`}>
                      <div className="flex items-center gap-3"><Clock className="h-5 w-5 text-[#F59E0B]" /><div><p className="font-semibold text-[#0A1914]">{p.course_title}</p><p className="text-xs text-gray-500 font-mono">TXID: {p.txid.slice(0, 24)}…</p></div></div>
                      <span className="text-xs font-mono uppercase text-[#F59E0B]">Awaiting admin</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {approved.length > 0 && (
              <div>
                <h2 className="font-display text-2xl text-[#0A1914] mb-4">Your courses</h2>
                <div className="grid lg:grid-cols-2 gap-6">
                  {approved.map((p) => (
                    <div key={p.id} className="border border-gray-200 bg-white p-5" data-testid={`course-${p.id}`}>
                      <div className="flex items-center gap-2 text-xs font-mono uppercase text-[#00B368] mb-3"><CheckCircle2 className="h-4 w-4" /> Active enrollment</div>
                      <MediaPlayer kind="course" itemId={p.course_id} image={COURSE_IMAGES[p.course_id]} label={p.course_title} role="Video course · Press play" caption={`Your full ${p.course_title} video training. Press play to begin.`} />
                      <PayoutTracker purchase={p} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {approved.length > 0 && (
              <div className="mt-10">
                <PayoutCalendar purchases={purchases} />
              </div>
            )}

            <div className="mt-12 grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-8">
                <ReferralCard />
                <LeaderboardPanel />
              </div>
              <div>
                <h2 className="font-display text-2xl text-[#0A1914] mb-1">Member Governance</h2>
                <p className="text-sm text-gray-600 mb-4">Our paying members are the CEO. Vote on proposals that shape Electronics-Galore.com.</p>
                <VotingPanel />
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
